import React, { useState, forwardRef, useImperativeHandle } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  INSERT_TABLE_COMMAND,
  $insertTableRowAtSelection,
  $insertTableColumnAtSelection,
  $deleteTableRowAtSelection,
  $deleteTableColumnAtSelection,
} from '@lexical/table';
import { WBIconButton, WBIcon, WBBox, WBSvgIcon } from '@admiin-com/ds-web';
import TableIcon from '../../../../../assets/icons/document-editor/table.svg';
import GearIcon from '../../../../../assets/icons/document-editor/gear.svg';
import PlusIcon from '../../../../../assets/icons/document-editor/plus.svg';
import TrashIcon from '../../../../../assets/icons/document-editor/trash.svg';
import { LexicalEditor, $getSelection, $isRangeSelection } from 'lexical';
import { VerticalDivider } from '../DocumentEditor.styles';
import LoadSvgIcon from '../../../../component/LoadSvgIcon/LoadSvgIcon';

export interface TableControlsHandle {
  openInsertTableModal: () => void;
}

interface TableControlsProps {
  editor: LexicalEditor;
  hideInsertButton?: boolean;
}

export const TableControls = forwardRef<
  TableControlsHandle,
  TableControlsProps
>(({ editor, hideInsertButton = false }, ref) => {
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [tableRows, setTableRows] = useState('3');
  const [tableCols, setTableCols] = useState('3');
  const [tableMenuAnchor, setTableMenuAnchor] = useState<null | HTMLElement>(
    null
  );

  const insertTable = () => {
    const rows = parseInt(tableRows) || 3;
    const cols = parseInt(tableCols) || 3;

    editor.dispatchCommand(INSERT_TABLE_COMMAND, {
      rows: String(rows),
      columns: String(cols),
    });

    setTableModalOpen(false);
  };

  const insertRowAbove = () => {
    editor.update(() => {
      $insertTableRowAtSelection(false);
    });
    setTableMenuAnchor(null);
  };

  const insertRowBelow = () => {
    editor.update(() => {
      $insertTableRowAtSelection(true);
    });
    setTableMenuAnchor(null);
  };

  const insertColumnBefore = () => {
    editor.update(() => {
      $insertTableColumnAtSelection(false);
    });
    setTableMenuAnchor(null);
  };

  const insertColumnAfter = () => {
    editor.update(() => {
      $insertTableColumnAtSelection(true);
    });
    setTableMenuAnchor(null);
  };

  const deleteRow = () => {
    editor.update(() => {
      $deleteTableRowAtSelection();
    });
    setTableMenuAnchor(null);
  };

  const deleteColumn = () => {
    editor.update(() => {
      $deleteTableColumnAtSelection();
    });
    setTableMenuAnchor(null);
  };

  useImperativeHandle(ref, () => ({
    openInsertTableModal: () => setTableModalOpen(true),
  }));

  return (
    <>
      {!hideInsertButton && (
        <WBIconButton
          size="large"
          onMouseDown={(e) => {
            e.preventDefault();
            setTableModalOpen(true);
          }}
          title="Insert Table"
          sx={{
            color: 'text.primary',
          }}
        >
          <LoadSvgIcon component={TableIcon} width={16} height={16} />
        </WBIconButton>
      )}

      {/* Table Helper Utilities */}
      <WBIconButton
        size="large"
        onMouseDown={(event) => {
          event.preventDefault();
          setTableMenuAnchor(event.currentTarget);
        }}
        title="Table Options"
        sx={{
          color: 'text.primary',
        }}
      >
        <LoadSvgIcon component={GearIcon} width={16} height={16} />
      </WBIconButton>

      <Menu
        anchorEl={tableMenuAnchor}
        open={Boolean(tableMenuAnchor)}
        onClose={() => setTableMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            '& .MuiMenuItem-root': {
              fontSize: '12px',
              minHeight: 30,
              padding: '6px 12px',
            },
          },
        }}
      >
        <MenuItem onClick={insertRowAbove}>
          <PlusIcon />
          &nbsp;Insert Row Above
        </MenuItem>
        <MenuItem onClick={insertRowBelow}>
          <PlusIcon />
          &nbsp;Insert Row Below
        </MenuItem>
        <MenuItem onClick={insertColumnBefore}>
          <PlusIcon />
          &nbsp;Insert Column Before
        </MenuItem>
        <MenuItem onClick={insertColumnAfter}>
          <PlusIcon />
          &nbsp;Insert Column After
        </MenuItem>
        <MenuItem onClick={deleteRow} sx={{ color: 'error.main' }}>
          <TrashIcon />
          &nbsp;Delete Row
        </MenuItem>
        <MenuItem onClick={deleteColumn} sx={{ color: 'error.main' }}>
          <TrashIcon />
          &nbsp;Delete Column
        </MenuItem>
      </Menu>

      <VerticalDivider orientation="vertical" flexItem />

      {/* Table Insert Modal */}
      <Dialog
        open={tableModalOpen}
        onClose={() => setTableModalOpen(false)}
        fullWidth
        maxWidth="xs"
        sx={{ '& .MuiDialog-paper': { minWidth: 360 } }}
      >
        <DialogTitle>Insert Table</DialogTitle>
        <DialogContent>
          <WBBox sx={{ display: 'flex', gap: 2, pt: 1 }}>
            <TextField
              label="Rows"
              type="number"
              value={tableRows}
              onChange={(e) => setTableRows(e.target.value)}
              size="small"
              inputProps={{ min: 1, max: 20 }}
            />
            <TextField
              label="Columns"
              type="number"
              value={tableCols}
              onChange={(e) => setTableCols(e.target.value)}
              size="small"
              inputProps={{ min: 1, max: 10 }}
            />
          </WBBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTableModalOpen(false)}>Cancel</Button>
          <Button onClick={insertTable} variant="contained">
            Insert
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});
